import React, { useState, useRef, useEffect } from 'react';
import { Input } from './input';
import { LabelDefinition } from '@/types/label-budget';
import { useCurrencyManager } from '@/hooks/useCurrencyManager';

interface EditableCellProps {
  value: string | number;
  type: LabelDefinition['type'];
  options?: string[];
  onChange: (value: string | number) => void;
  onSave?: () => void;
  disabled?: boolean;
  isReadOnly?: boolean;
  employeeField?: LabelDefinition['employeeField'];
  className?: string;
  labelName?: string;
  budgetInfo?: {
    usage: number;
    limit: number;
    percentage: number;
  };
}

export const EditableCell: React.FC<EditableCellProps> = React.memo(({
  value,
  type,
  options,
  onChange,
  onSave,
  disabled = false,
  isReadOnly = false,
  employeeField,
  className = '',
  labelName,
  budgetInfo
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(value.toString());
  const inputRef = useRef<HTMLInputElement>(null);
  const selectRef = useRef<HTMLSelectElement>(null);
  const { formatAmountSync } = useCurrencyManager();

  useEffect(() => {
    if (isEditing) {
      if (type === 'select' && selectRef.current) {
        selectRef.current.focus();
      } else if (inputRef.current) {
        inputRef.current.focus();
        inputRef.current.select();
      }
    }
  }, [isEditing, type]);

  const handleClick = () => {
    if (!disabled && !isReadOnly && !isEditing) {
      setIsEditing(true);
      setEditValue(value.toString());
    }
  };

  const handleSave = () => {
    let finalValue: string | number = editValue;
    
    if (type === 'number' || type === 'currency') {
      const numValue = parseFloat(editValue);
      if (!isNaN(numValue)) {
        finalValue = numValue;
      }
    }

    onChange(finalValue);
    setIsEditing(false);
    onSave?.();
  };

  const handleCancel = () => {
    setEditValue(value.toString());
    setIsEditing(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSave();
    } else if (e.key === 'Escape') {
      handleCancel();
    }
  };

  const renderBudgetIndicator = () => {
    if (!budgetInfo || type !== 'currency') return null;
    
    const { percentage } = budgetInfo;
    let color = 'text-green-600 dark:text-green-400';
    let bgColor = 'bg-green-100 dark:bg-green-900/20';
    let icon = '✓';
    
    if (percentage >= 100) {
      color = 'text-red-600 dark:text-red-400';
      bgColor = 'bg-red-100 dark:bg-red-900/20';
      icon = '⚠';
    } else if (percentage >= 80) {
      color = 'text-amber-600 dark:text-amber-400';
      bgColor = 'bg-amber-100 dark:bg-amber-900/20';
      icon = '!';
    }

    return (
      <span className={`ml-2 px-2 py-0.5 text-xs rounded-full ${color} ${bgColor}`}>
        {icon} {percentage.toFixed(0)}%
      </span>
    );
  };

  if (isEditing) {
    if (type === 'select' && options) {
      return (
        <select
          ref={selectRef}
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          onBlur={handleSave}
          onKeyDown={handleKeyDown}
          className={`px-2 py-1 text-sm bg-white dark:bg-gray-800 border border-blue-500 rounded outline-none ${className}`}
        >
          <option value="">--</option>
          {options.map((opt) => (
            <option key={opt} value={opt}>{opt}</option>
          ))}
        </select>
      );
    }

    return (
      <Input
        ref={inputRef}
        type={type === 'currency' || type === 'number' ? 'number' : 'text'}
        value={editValue}
        onChange={(e) => setEditValue(e.target.value)}
        onBlur={handleSave}
        onKeyDown={handleKeyDown}
        className={`px-2 py-1 text-sm border-blue-500 ${className}`}
        step={type === 'currency' ? '0.01' : undefined}
      />
    );
  }

  const displayValue = () => {
    if (!value && value !== 0) return <span className="text-gray-400">--</span>;
    
    if (type === 'currency') {
      return (
        <span className="flex items-center">
          {formatAmountSync(Number(value))}
          {renderBudgetIndicator()}
        </span>
      );
    }
    
    // For employee type, format special fields
    if (type === 'employee' && employeeField === 'joinDate' && value) {
      const dateStr = value.toString();
      if (dateStr.match(/^\d{4}-\d{2}-\d{2}/)) {
        return new Date(dateStr).toLocaleDateString();
      }
    }

    if (type === 'employee' && employeeField === 'department') {
      const textValue = value.toString();
      return (
        <span className="block whitespace-pre-line leading-tight">
          {textValue}
        </span>
      );
    }
    
    return value.toString();
  };

  const getTooltipText = () => {
    if (disabled || isReadOnly) return undefined;
    
    let tooltipText = labelName ? `${labelName}: ` : '';
    
    if (type === 'employee') {
      tooltipText += 'Auto-populated from employee data';
      return tooltipText;
    }
    
    if (!value && value !== 0) {
      tooltipText += 'Click to add value';
    } else {
      if (type === 'currency') {
        tooltipText += `Current: ${formatAmountSync(Number(value))}`;
        if (budgetInfo) {
          tooltipText += ` (${budgetInfo.percentage.toFixed(0)}% of budget)`;
        }
      } else {
        tooltipText += `Current: ${value}`;
      }
      tooltipText += ' - Click to edit';
    }
    
    return tooltipText;
  };

  return (
    <div
      onClick={handleClick}
      className={`
        px-2 py-1 min-h-[32px] flex items-center group relative
        ${!disabled && !isReadOnly ? 'cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors' : ''}
        ${type === 'employee' ? 'text-gray-600 dark:text-gray-400 italic' : ''}
        ${className}
      `}
      title={getTooltipText()}
    >
      {displayValue()}
      {!disabled && !isReadOnly && type !== 'employee' && (
        <span className="absolute left-0 top-full mt-1 hidden group-hover:block z-10 px-2 py-1 text-xs bg-gray-900 text-white rounded shadow-lg whitespace-nowrap">
          {!value && value !== 0 ? 'Click to add' : 'Click to edit'}
          {type === 'select' && options && ` (${options.length} options)`}
          {type === 'currency' && ' currency value'}
          {type === 'number' && ' number'}
          {type === 'text' && ' text'}
        </span>
      )}
      {type === 'employee' && value && (
        <svg className="w-3 h-3 ml-1 text-blue-500 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
        </svg>
      )}
    </div>
  );
});
EditableCell.displayName = 'EditableCell';
